import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import type { PortalConfig } from '@/api/vendo';
import {
   uploadAudio,
   uploadImage,
   uploadVideoAsset,
   listMedia,
   registerMedia,
   deleteMedia,
   getLayoutDefaults,
   type MediaAsset,
} from '@/api/store';
import { listDevices, updateDevice, syncSubPortalThemesApi } from '@/api/vendo';
import {
   PORTAL_LAYOUTS,
   PORTAL_EFFECTS,
   BG_PRESETS,
   HOTEL_ACCENTS,
   BEACH_ACCENTS,
   type LayoutMeta,
   applyLayoutPick,
   resetLayoutToDefault,
} from './portalVibes';
import { pauseOtherAudio } from '@/utils/pauseOtherAudio';

interface Props {
   open: boolean;
   value: PortalConfig;
   onChange: (next: PortalConfig) => void;
   onClose: () => void;
   /** Persist immediately (DeviceSettings wires this to updateDevice). */
   onSave?: () => Promise<void> | void;
   /** Open the live captive portal preview for this device. */
   onPreview?: () => void;
   saving?: boolean;
   /** Rendered above the action buttons — the "sending to the router / live"
    *  banner after a save (Ray, 2026-09-15). */
   progress?: React.ReactNode;
   /** Admin layout-defaults editor: the layout is fixed — hide the
       Layout tab and skip applying pick-time defaults. */
   hideLayoutTab?: boolean;
   /** The device being edited — enables "Apply to other vendos" (the
       current design is written to selected other main vendos). */
   deviceId?: string;
}

/* Video backgrounds: built + staged 2026-07-06, hidden until R2 had a custom
   domain — r2.dev throttling made playback stall. Enabled 2026-07-29 now that
   media is served from smartficdn.site, which also became the moment this
   stopped being optional: the animated-GIF backgrounds are 6-8MB each and
   Cloudinary used to shrink them on delivery. As MP4 they are ~0.5MB, so the
   library's animated backgrounds now live here. */
const VIDEO_BG_ENABLED = true;

type DrawerTab =
   | 'layout'
   | 'styles'
   | 'background'
   | 'slider'
   | 'sounds'
   | 'effects';

const TABS: { key: DrawerTab; label: string }[] = [
   { key: 'layout', label: 'Layout' },
   { key: 'styles', label: 'Styles' },
   { key: 'background', label: 'Background' },
   { key: 'slider', label: 'Slider' },
   { key: 'sounds', label: 'Sounds' },
   { key: 'effects', label: 'Effects' },
];

/** The three main action buttons the Styles tab can customize. */
const STYLE_TARGETS: {
   key: 'insertCoin' | 'wifiRates' | 'voucher';
   name: string;
   defaultLabel: string;
   defaultColor: string;
}[] = [
   { key: 'insertCoin', name: 'Insert Money button', defaultLabel: 'Insert Money', defaultColor: '#43a047' },
   { key: 'wifiRates', name: 'Wifi Rates button', defaultLabel: 'Wifi Rates', defaultColor: '#1e88e5' },
   { key: 'voucher', name: 'Voucher button', defaultLabel: 'Voucher', defaultColor: '#007dfe' },
];

const STYLE_ICONS: { icon: string; name: string }[] = [
   { icon: '🪙', name: 'Coin' },
   { icon: '💵', name: 'Cash' },
   { icon: '⚡', name: 'Bolt' },
   { icon: '📶', name: 'Signal' },
   { icon: '🎫', name: 'Ticket' },
   { icon: '💳', name: 'Card' },
   { icon: '⭐', name: 'Star' },
   { icon: '🔥', name: 'Flame' },
];

/** Gaming layout: Insert Coin orb decoration styles. */
const COIN_DECORS: { key: string; name: string; description: string; swatch: string }[] = [
   {
      key: '',
      name: 'Neon Orb',
      description: 'Cyan/magenta energy swirls with orbiting sparks. Default.',
      swatch: 'radial-gradient(circle at 35% 35%, #00e5ff 0%, #7a5cff 45%, #0b1120 75%)',
   },
   {
      key: 'solar',
      name: 'Solar Flare',
      description: 'Fiery flare ring with drifting embers. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #ffd27a 0%, #ff8c1a 45%, #1a0e02 78%)',
   },
   {
      key: 'swirl',
      name: 'Infinite Swirl',
      description: 'Cyan-violet galaxy swirl circling the orb. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #cfeaff 0%, #7a5cff 50%, #10142a 80%)',
   },
   {
      key: 'elemental',
      name: 'Balance',
      description: 'Fire, rock, water & electricity revolve around the orb. Animated.',
      swatch: 'linear-gradient(135deg, #ffb347 0%, #ff8c1a 42%, #7cc7ff 58%, #cfeaff 100%)',
   },
   {
      key: 'cycling',
      name: 'Cycling Lights',
      description: 'Rainbow glowing ring with sweeping rays. Animated.',
      swatch: 'conic-gradient(#ff4d4d, #ffb800, #4dff88, #4dc3ff, #b44dff, #ff4d4d)',
   },
   {
      key: 'ripples',
      name: 'Ambient Ripples',
      description: 'Light-blue waves dancing around the orb. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #e8f6ff 0%, #7cc7ff 50%, #0d1b30 82%)',
   },
   {
      key: 'cat-ears',
      name: 'Cat Ears',
      description: 'Pink kitty ears and whiskers on the orb. Animated.',
      swatch: 'radial-gradient(circle at 35% 30%, #ffe4ec 0%, #f2a7c3 45%, #fff 80%)',
   },
   {
      key: 'magic',
      name: 'Magic Ring',
      description: 'Enchanted blue-violet ring of light. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #9fd8ff 0%, #5a6cff 50%, #131a3a 82%)',
   },
   {
      key: 'match-day',
      name: 'Match Day',
      description: 'Spinning football wrapped around the orb. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #f4f4f4 0%, #8f7dff 55%, #241b4d 85%)',
   },
   {
      key: 'moon',
      name: 'Moon',
      description: 'Glowing purple crescent moon. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #f0dcff 0%, #b98ae8 50%, #2a1738 82%)',
   },
   {
      key: 'star-stuck',
      name: 'Star Stuck',
      description: 'Dreamy clouds, moon and twinkling stars. Animated.',
      swatch: 'radial-gradient(circle at 35% 35%, #ffffff 0%, #9fd0f7 50%, #16324f 82%)',
   },
   {
      key: 'ring',
      name: 'Simple Ring',
      description: 'Clean spinning rainbow rim — no extra effects.',
      swatch: 'conic-gradient(#ff2e88, #ffb800, #39ff9c, #00e5ff, #7a5cff, #ff2e88)',
   },
];
const STYLE_SHAPES: { key: string; name: string }[] = [
   { key: '', name: 'Default' },
   { key: 'pill', name: 'Pill' },
   { key: 'outline', name: 'Outline' },
];

const PortalAdvancedDrawer: React.FC<Props> = ({
   open,
   value,
   onChange,
   onClose,
   onSave,
   onPreview,
   saving = false,
   progress,
   hideLayoutTab = false,
   deviceId,
}) => {
   const [tab, setTab] = useState<DrawerTab>(hideLayoutTab ? 'styles' : 'layout');
   // "Apply to other vendos" (Ray 2026-07-07): a second screen listing the
   // operator's OTHER main vendos — the current design is written to the
   // checked ones. Sub-vendos inherit their main's config, so only mains
   // are listed.
   const [view, setView] = useState<'editor' | 'apply'>('editor');
   const [applyChecked, setApplyChecked] = useState<Set<string>>(new Set());
   const [applying, setApplying] = useState(false);
   // Per-layout reset: inline "it worked" note — the Layout tab itself
   // shows none of the fields the reset changes, so without this the
   // click looks like a no-op.
   const [resetDone, setResetDone] = useState(false);
   useEffect(() => {
      if (open) {
         setView('editor');
         setApplyChecked(new Set());
         setResetDone(false);
      }
   }, [open]);
   const [videoUploading, setVideoUploading] = useState(false);
   const dragIdxRef = useRef<number | null>(null);
   const queryClient = useQueryClient();
   // This drawer is the OPERATOR surface: delete shows only on their own
   // uploads. Global library assets are managed (and deleted) exclusively
   // in the admin panel's Library — even for admin accounts, so a global
   // asset can't be nuked by accident while theming one device.
   const canDeleteAsset = (a: MediaAsset) => !a.global;

   // Media library: admin-curated global assets + this operator's own
   // uploads, one fetch for all three galleries.
   const { data: mediaData } = useQuery({
      queryKey: ['portal-media'],
      queryFn: () => listMedia(),
      enabled: open,
      staleTime: 30_000,
   });
   const assets = mediaData?.assets ?? [];
   // Admin per-layout defaults — pre-fill background/color/sound/effects/
   // slides when the operator PICKS a layout (not in the defaults editor).
   // staleTime 0: an admin edits these in another tab and expects the next
   // pick/reset to use the new look — a stale copy makes reset a no-op.
   const {
      data: layoutDefaults,
      isError: defaultsFailed,
      refetch: refetchDefaults,
   } = useQuery({
      queryKey: ['layout-defaults'],
      queryFn: () => getLayoutDefaults(),
      enabled: open && !hideLayoutTab,
      staleTime: 0,
   });
   // Layout cards + Reset wait for the defaults. Pressing either before the
   // fetch landed used to silently apply the plain built-in look — the
   // operator's "sometimes my admin default is missing" (Ray 2026-09-14).
   const defaultsReady = hideLayoutTab || !!layoutDefaults;
   const curatedFor = (layout: string) =>
      layoutDefaults?.defaults?.[layout] as Partial<PortalConfig> | undefined;
   // Other main vendos this design can be copied to.
   const { data: devicesData } = useQuery({
      queryKey: ['devices'],
      queryFn: () => listDevices(),
      enabled: open && view === 'apply',
      staleTime: 30_000,
   });
   const applyTargets = (devicesData?.devices ?? []).filter(
      (d) => !d.parent && d._id !== deviceId
   );

   const toggleApplyTarget = (id: string) =>
      setApplyChecked((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });

   const applyToOthers = async () => {
      const ids = [...applyChecked];
      if (ids.length === 0) {
         toast.error('Pick at least one vendo first');
         return;
      }
      setApplying(true);
      let ok = 0;
      let failed = 0;
      for (const id of ids) {
         try {
            await updateDevice(id, { portalTheme: 'custom', portalConfig: value });
            ok++;
         } catch {
            failed++;
         }
      }
      setApplying(false);
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      if (ok) toast.success(`Design applied to ${ok} vendo${ok === 1 ? '' : 's'}`);
      if (failed) toast.error(`${failed} vendo${failed === 1 ? '' : 's'} failed — try again`);
      if (!failed) {
         setApplyChecked(new Set());
         setView('editor');
      }
   };

   const bgAssets = assets.filter((a) => a.kind === 'background');
   const sliderAssets = assets.filter((a) => a.kind === 'slider');
   const soundAssets = assets.filter((a) => a.kind === 'sound');
   // Per-slot sound lists: categorized sounds only show in their slot;
   // uncategorized ('' — legacy uploads) show in both.
   const insertCoinSounds = soundAssets.filter(
      (a) => !a.category || a.category === 'insert-coin'
   );
   const countdownSounds = soundAssets.filter(
      (a) => !a.category || a.category === 'countdown'
   );
   const videoAssets = assets.filter((a) => a.kind === 'video');

   if (!open) return null;

   const refreshMedia = () =>
      queryClient.invalidateQueries({ queryKey: ['portal-media'] });

   const setBg = (patch: Partial<PortalConfig['background']>) =>
      onChange({ ...value, background: { ...value.background, ...patch } });
   const setSlider = (patch: Partial<PortalConfig['slider']>) =>
      onChange({ ...value, slider: { ...value.slider, ...patch } });
   const setAudio = (patch: Partial<PortalConfig['audio']>) =>
      onChange({ ...value, audio: { ...value.audio, ...patch } });

   const pickLayout = (l: LayoutMeta) => {
      if (!defaultsReady) return;
      setResetDone(false);
      onChange(
         applyLayoutPick(
            value,
            l.key,
            curatedFor(l.key),
            curatedFor(value.layout || 'classic')
         )
      );
   };

   const resetCurrentLayout = () => {
      if (!defaultsReady) return;
      const layout = value.layout || 'classic';
      // Apply instantly with the loaded defaults. An await here would leave
      // a dead-feeling gap where a quick Save races the reset.
      onChange(resetLayoutToDefault(value, curatedFor(layout)));
      setResetDone(true);
      toast.success(
         `${PORTAL_LAYOUTS.find((l) => l.key === layout)?.name || 'Layout'} reset to default`
      );
   };

   const setButtonStyle = (
      target: 'insertCoin' | 'wifiRates' | 'voucher',
      patch: Partial<{ label: string; color: string; icon: string; shape: string; style: string }>
   ) =>
      onChange({
         ...value,
         buttons: {
            ...(value.buttons || {}),
            [target]: { ...(value.buttons?.[target] || {}), ...patch },
         },
      });

   const toggleEffect = (key: string) => {
      const has = value.effects.includes(key);
      onChange({
         ...value,
         effects: has
            ? value.effects.filter((e) => e !== key)
            : [...value.effects, key],
      });
   };

   const toggleSliderImage = (url: string) => {
      const has = value.slider.images.includes(url);
      setSlider({
         images: has
            ? value.slider.images.filter((u) => u !== url)
            : [...value.slider.images, url],
      });
   };

   const moveSliderImage = (from: number, to: number) => {
      if (to < 0 || to >= value.slider.images.length || from === to) return;
      const next = [...value.slider.images];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setSlider({ images: next });
   };

   // Delete one of the operator's OWN uploads from their library — also
   const handleVideoUpload = async (file: File) => {
      if (!/^video\/(mp4|webm)$/.test(file.type)) {
         toast.error('Only MP4 or WebM videos are supported');
         return;
      }
      if (file.size > 15 * 1024 * 1024) {
         toast.error('Video is over 15MB — trim or compress the loop first');
         return;
      }
      setVideoUploading(true);
      try {
         const asset = await uploadVideoAsset(file);
         setBg({ mode: 'video', videoUrl: asset.url });
         refreshMedia();
         toast.success('Video uploaded');
      } catch {
         toast.error('Video upload failed — please try again');
      } finally {
         setVideoUploading(false);
      }
   };

   // unchecks it anywhere the config still points at it.
   const deleteOwnAsset = async (a: MediaAsset) => {
      try {
         await deleteMedia(a._id);
         const next = { ...value };
         if (value.slider.images.includes(a.url)) {
            next.slider = {
               ...value.slider,
               images: value.slider.images.filter((u) => u !== a.url),
            };
         }
         if (value.background.imageUrl === a.url) {
            next.background = { ...value.background, imageUrl: '' };
         }
         if (value.background.videoUrl === a.url) {
            next.background = { ...next.background, videoUrl: '' };
         }
         if (value.audio.insertCoinMp3 === a.url) {
            next.audio = { ...next.audio, insertCoinMp3: '' };
         }
         if (value.audio.countdownMp3 === a.url) {
            next.audio = { ...next.audio, countdownMp3: '' };
         }
         onChange(next);
         refreshMedia();
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Delete failed');
      }
   };

   // URLs saved in the config that pre-date the media library (uploaded
   // through the old drawer) — render them as tiles so they stay visible
   // and can be unchecked; otherwise they'd rotate on the portal with no
   // way to remove them here.
   const legacySliderUrls = value.slider.images.filter(
      (u) => !sliderAssets.some((a) => a.url === u)
   );
   const legacyBgUrl =
      value.background.imageUrl &&
      !bgAssets.some((a) => a.url === value.background.imageUrl)
         ? value.background.imageUrl
         : '';

   const body = (
      <Overlay onClick={onClose}>
         <Dialog onClick={(e) => e.stopPropagation()}>
            <Head>
               <div>
                  <Title>Advanced portal options</Title>
                  <Sub>
                     {view === 'apply'
                        ? 'Copy this design to other vendos'
                        : 'Layout, background, slider, sounds & effects'}
                  </Sub>
               </div>
               <Close onClick={onClose} aria-label="Close">
                  ×
               </Close>
            </Head>

            {view === 'apply' ? (
            <>
            <Bodied>
               <Section>
                  <SecTitle>Apply to other vendos</SecTitle>
                  <SecHint>
                     The current design (layout, background, sounds, effects,
                     slides, buttons) is copied to the vendos you check.
                     Their old design is replaced. Sub-vendos follow their
                     main vendo automatically.
                  </SecHint>
                  {applyTargets.map((d) => (
                     <ApplyRow
                        key={d._id}
                        type="button"
                        $checked={applyChecked.has(d._id)}
                        onClick={() => toggleApplyTarget(d._id)}
                     >
                        <SoundCheck $checked={applyChecked.has(d._id)}>
                           {applyChecked.has(d._id) ? '✓' : ''}
                        </SoundCheck>
                        <ApplyName>{d.name || d.deviceId}</ApplyName>
                     </ApplyRow>
                  ))}
                  {applyTargets.length === 0 && (
                     <ApplyEmpty>No other main vendos on this account.</ApplyEmpty>
                  )}
                  {/* Sub-vendos of THIS main: they follow it automatically —
                      this button re-syncs any sub that was given its own
                      design, so the whole cluster matches again. */}
                  <SecTitle style={{ marginTop: 16 }}>Sub-vendos</SecTitle>
                  <SecHint>
                     Copies this vendo's SAVED design to all of its
                     sub-vendos, one time. Subs keep their own design until
                     you press this. Save your changes first.
                  </SecHint>
                  <ApplyRow
                     type="button"
                     $checked={false}
                     disabled={applying}
                     onClick={async () => {
                        try {
                           const r = await syncSubPortalThemesApi(deviceId!);
                           toast.success(
                              r.subs > 0
                                 ? `Design copied to ${r.subs} sub-vendo${r.subs === 1 ? '' : 's'}`
                                 : 'No sub-vendos on this vendo'
                           );
                        } catch (e: any) {
                           toast.error(e?.response?.data?.error || 'Could not sync sub-vendos');
                        }
                     }}
                  >
                     <ApplyName>↺ Apply to all my sub-vendos</ApplyName>
                  </ApplyRow>
               </Section>
            </Bodied>
            <Foot>
               <GhostBtn type="button" onClick={() => setView('editor')}>
                  ‹ Back
               </GhostBtn>
               <Spacer />
               <PrimaryBtn
                  type="button"
                  onClick={applyToOthers}
                  disabled={applying || applyChecked.size === 0}
               >
                  {applying
                     ? 'Applying…'
                     : `Apply to ${applyChecked.size || '…'} vendo${applyChecked.size === 1 ? '' : 's'}`}
               </PrimaryBtn>
            </Foot>
            </>
            ) : (
            <>
            <TabBar>
               {TABS.filter((t) => !(hideLayoutTab && t.key === 'layout')).map((t) => (
                  <TabBtn
                     key={t.key}
                     type="button"
                     $active={tab === t.key}
                     onClick={() => setTab(t.key)}
                  >
                     {t.label}
                  </TabBtn>
               ))}
            </TabBar>

            <Bodied>
               {/* ── Layout ───────────────────────────────────────── */}
               {tab === 'layout' && (
               <Section>
                  {/* Reset lives top-right, small and out of the way —
                      it was too easy to hit accidentally under the grid. */}
                  <ResetTopRow>
                     {/* Per-layout reset: back to the admin default / clean
                         look and this layout's remembered changes are wiped. */}
                     <LayoutResetLink
                        type="button"
                        onClick={resetCurrentLayout}
                        disabled={!defaultsReady}
                     >
                        ↺ Reset{' '}
                        {PORTAL_LAYOUTS.find((l) => l.key === (value.layout || 'classic'))
                           ?.name || 'this layout'}{' '}
                        to default
                     </LayoutResetLink>
                  </ResetTopRow>
                  {!defaultsReady && (
                     <DefaultsNote $failed={defaultsFailed}>
                        {defaultsFailed ? (
                           <>
                              Could not load your defaults.{' '}
                              <RetryLink type="button" onClick={() => refetchDefaults()}>
                                 Retry
                              </RetryLink>
                           </>
                        ) : (
                           'Loading your defaults…'
                        )}
                     </DefaultsNote>
                  )}
                  {resetDone && (
                     <ResetDoneNote>
                        ✓ This layout is back to its default design — check
                        the other tabs to see it. Press “Save changes” to
                        show it on your portal.
                     </ResetDoneNote>
                  )}
                  <SecHint>
                     Each layout is a different portal design — its own page
                     structure, buttons and frames, not just colors. More
                     layouts are coming one at a time.
                  </SecHint>
                  <LayoutGrid>
                     {PORTAL_LAYOUTS.map((l) => (
                        <LayoutCard
                           key={l.key || 'default'}
                           type="button"
                           $active={(value.layout || '') === l.key}
                           onClick={() => pickLayout(l)}
                           disabled={!defaultsReady}
                        >
                           <LayoutSwatch style={{ background: l.swatch.bg }}>
                              <SwatchStrip style={{ background: l.swatch.bar }} />
                              <SwatchDot style={{ background: l.swatch.accent }} />
                              <SwatchBar style={{ background: l.swatch.bar }} />
                              <SwatchBar
                                 style={{ background: l.swatch.bar, opacity: 0.55 }}
                              />
                           </LayoutSwatch>
                           <LayoutText>
                              <LayoutName>{l.name}</LayoutName>
                              <LayoutDesc>{l.description}</LayoutDesc>
                           </LayoutText>
                        </LayoutCard>
                     ))}
                  </LayoutGrid>
               </Section>
               )}

               {tab === 'layout' && (
               <Section>
                  <SecTitle>Company name</SecTitle>
                  <SecHint>
                     Shown at the bottom of your portal. Leave blank for
                     "Powered by SmartFi".
                  </SecHint>
                  <TextInput
                     type="text"
                     maxLength={40}
                     placeholder="e.g. Joe's WiFi"
                     value={value.brand?.companyName ?? ''}
                     onChange={(e) =>
                        onChange({
                           ...value,
                           brand: { ...(value.brand || {}), companyName: e.target.value },
                        })
                     }
                  />
                  <SecHint>
                     Preview: Powered by{' '}
                     <b>
                        {value.brand?.companyName?.trim()
                           ? `${value.brand.companyName.trim()} & `
                           : ''}
                        SmartFi
                     </b>
                  </SecHint>
               </Section>
               )}

               {/* ── Styles (main action buttons) ─────────────────── */}
               {tab === 'styles' && value.layout === 'gaming' && (
               <Section>
                  <SecTitle>Insert Coin button style</SecTitle>
                  <SecHint>
                     Pick the animated decoration for the big Insert Coin
                     orb. The other gaming buttons keep their signature
                     neon look.
                  </SecHint>
                  <LayoutGrid>
                     {COIN_DECORS.map((d) => (
                        <LayoutCard
                           key={d.key || 'neon'}
                           type="button"
                           $active={(value.buttons?.insertCoin?.style || '') === d.key}
                           onClick={() => setButtonStyle('insertCoin', { style: d.key })}
                        >
                           <DecorSwatch style={{ background: d.swatch }} />
                           <LayoutText>
                              <LayoutName>{d.name}</LayoutName>
                              <LayoutDesc>{d.description}</LayoutDesc>
                           </LayoutText>
                        </LayoutCard>
                     ))}
                  </LayoutGrid>
               </Section>
               )}
               {tab === 'styles' && value.layout === 'hotel' && (
               <Section>
                  <SecTitle>Color theme</SecTitle>
                  <SecHint>
                     Pick the accent your buttons, timer and details wear —
                     champagne gold is the signature, but every palette
                     keeps the same elegant chrome.
                  </SecHint>
                  <PresetGrid>
                     {HOTEL_ACCENTS.map((a) => {
                        const active = (value.accent || '') === a.key;
                        return (
                           <PresetTile
                              key={a.name}
                              type="button"
                              $active={active}
                              title={a.name}
                              onClick={() =>
                                 onChange({ ...value, accent: a.key })
                              }
                           >
                              <AccentSwatch style={{ background: a.hex }}>
                                 <AccentDot />
                              </AccentSwatch>
                              <PresetName>{a.name}</PresetName>
                           </PresetTile>
                        );
                     })}
                  </PresetGrid>
               </Section>
               )}
               {tab === 'styles' && value.layout === 'beach' && (
               <Section>
                  <SecTitle>Button color</SecTitle>
                  <SecHint>
                     Pick the summer color for your action buttons — the
                     sea-blue icons, timer and prices stay part of the
                     beach signature.
                  </SecHint>
                  <PresetGrid>
                     {BEACH_ACCENTS.map((a) => {
                        const active = (value.accent || '') === a.key;
                        return (
                           <PresetTile
                              key={a.name}
                              type="button"
                              $active={active}
                              title={a.name}
                              onClick={() =>
                                 onChange({ ...value, accent: a.key })
                              }
                           >
                              <AccentSwatch style={{ background: a.hex }}>
                                 <AccentDot />
                              </AccentSwatch>
                              <PresetName>{a.name}</PresetName>
                           </PresetTile>
                        );
                     })}
                  </PresetGrid>
               </Section>
               )}
               {tab === 'styles' &&
                  value.layout !== 'gaming' &&
                  value.layout !== 'hotel' &&
                  value.layout !== 'beach' && (
               <>
                  <SecHint>
                     Customize the three main buttons — label, color, icon
                     and shape. Leave a field empty to keep the default.
                  </SecHint>
                  {STYLE_TARGETS.map((t) => {
                     const cur = value.buttons?.[t.key] || {};
                     const customized = !!(
                        cur.label ||
                        cur.color ||
                        cur.icon ||
                        cur.shape
                     );
                     return (
                        <StyleCard key={t.key}>
                           <StyleCardHead>
                              <StyleCardTitle>{t.name}</StyleCardTitle>
                              {customized && (
                                 <ResetLink
                                    type="button"
                                    onClick={() =>
                                       setButtonStyle(t.key, {
                                          label: '',
                                          color: '',
                                          icon: '',
                                          shape: '',
                                       })
                                    }
                                 >
                                    Reset
                                 </ResetLink>
                              )}
                           </StyleCardHead>

                           {/* Live preview — this IS the button being
                               edited, updating as you type. */}
                           <BtnPreviewWrap>
                              <BtnPreview
                                 $shape={cur.shape || ''}
                                 $color={cur.color || t.defaultColor}
                              >
                                 {cur.icon ? `${cur.icon} ` : ''}
                                 {cur.label || t.defaultLabel}
                              </BtnPreview>
                           </BtnPreviewWrap>

                           <StyleGrid>
                              <StyleField>
                                 <FieldLabel>Label</FieldLabel>
                                 <TextInput
                                    value={cur.label || ''}
                                    onChange={(e) =>
                                       setButtonStyle(t.key, { label: e.target.value })
                                    }
                                    placeholder={t.defaultLabel}
                                    maxLength={24}
                                 />
                              </StyleField>
                              <StyleField>
                                 <FieldLabel>Color</FieldLabel>
                                 <ColorFieldRow>
                                    <ColorSwatch
                                       type="color"
                                       value={cur.color || t.defaultColor}
                                       onChange={(e) =>
                                          setButtonStyle(t.key, { color: e.target.value })
                                       }
                                    />
                                    {cur.color && (
                                       <MiniReset
                                          type="button"
                                          title="Reset color"
                                          onClick={() =>
                                             setButtonStyle(t.key, { color: '' })
                                          }
                                       >
                                          ↺
                                       </MiniReset>
                                    )}
                                 </ColorFieldRow>
                              </StyleField>
                              <StyleField>
                                 <FieldLabel>Icon</FieldLabel>
                                 <SelectInput
                                    value={cur.icon || ''}
                                    onChange={(e) =>
                                       setButtonStyle(t.key, { icon: e.target.value })
                                    }
                                 >
                                    <option value="">None</option>
                                    {STYLE_ICONS.map((ic) => (
                                       <option key={ic.icon} value={ic.icon}>
                                          {ic.icon} {ic.name}
                                       </option>
                                    ))}
                                 </SelectInput>
                              </StyleField>
                              <StyleField>
                                 <FieldLabel>Shape</FieldLabel>
                                 <SelectInput
                                    value={cur.shape || ''}
                                    onChange={(e) =>
                                       setButtonStyle(t.key, { shape: e.target.value })
                                    }
                                 >
                                    {STYLE_SHAPES.map((sh) => (
                                       <option key={sh.key || 'default'} value={sh.key}>
                                          {sh.name}
                                       </option>
                                    ))}
                                 </SelectInput>
                              </StyleField>
                           </StyleGrid>
                        </StyleCard>
                     );
                  })}
               </>
               )}

               {/* ── Background ───────────────────────────────────── */}
               {tab === 'background' && (
               <Section>
                  <SegToggle>
                     <SegBtn
                        type="button"
                        $active={value.background.mode === 'color'}
                        onClick={() => setBg({ mode: 'color' })}
                     >
                        Color
                     </SegBtn>
                     <SegBtn
                        type="button"
                        $active={value.background.mode === 'image'}
                        onClick={() => setBg({ mode: 'image' })}
                     >
                        Image
                     </SegBtn>
                     {(VIDEO_BG_ENABLED ||
                        value.background.mode === 'video') && (
                        <SegBtn
                           type="button"
                           $active={value.background.mode === 'video'}
                           onClick={() => setBg({ mode: 'video' })}
                        >
                           Video
                        </SegBtn>
                     )}
                  </SegToggle>

                  {value.background.mode === 'color' ? (
                     <>
                        <SecHint>Pick a ready-made style, or set your own color below.</SecHint>
                        <PresetGrid>
                           {BG_PRESETS.map((pr) => {
                              const active = pr.gradient
                                 ? value.background.gradient === pr.gradient
                                 : !value.background.gradient &&
                                   value.background.color === pr.base;
                              return (
                                 <PresetTile
                                    key={pr.name}
                                    type="button"
                                    $active={active}
                                    title={pr.name}
                                    onClick={() =>
                                       setBg({
                                          mode: 'color',
                                          color: pr.base,
                                          gradient: pr.gradient,
                                       })
                                    }
                                 >
                                    <PresetSwatch
                                       style={{ background: pr.gradient || pr.base }}
                                    />
                                    <PresetName>{pr.name}</PresetName>
                                 </PresetTile>
                              );
                           })}
                        </PresetGrid>
                        <FieldLabel>Custom color</FieldLabel>
                        <Row>
                           <ColorSwatch
                              type="color"
                              value={value.background.color}
                              onChange={(e) =>
                                 setBg({ color: e.target.value, gradient: '' })
                              }
                           />
                           <TextInput
                              value={value.background.color}
                              onChange={(e) =>
                                 setBg({ color: e.target.value, gradient: '' })
                              }
                              placeholder="#0e1116"
                           />
                        </Row>
                     </>
                  ) : value.background.mode === 'video' ? (
                     <>
                        <SecHint>
                           A short looping video plays behind the portal
                           (muted). Tap one to use it.
                        </SecHint>
                        <ImageGallery $portrait>
                           <VideoUploadTile
                              busy={videoUploading}
                              onPick={handleVideoUpload}
                           />
                           {videoAssets.map((a) => (
                              <GalleryItem
                                 key={a._id}
                                 type="button"
                                 $checked={value.background.videoUrl === a.url}
                                 onClick={() =>
                                    setBg({ mode: 'video', videoUrl: a.url })
                                 }
                              >
                                 {/* Previews play so the operator sees the
                                     motion they are picking, not a frozen
                                     first frame. Muted + playsInline are what
                                     make autoplay allowed; loop keeps every
                                     tile alive instead of stopping after one
                                     pass. */}
                                 <GalleryVideo
                                    src={a.url}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    preload="metadata"
                                 />
                                 {value.background.videoUrl === a.url && (
                                    <CheckBadge>✓</CheckBadge>
                                 )}
                                 {!a.global && <MineBadge>Mine</MineBadge>}
                                 {canDeleteAsset(a) && (
                                    <TileDelBtn
                                       type="button"
                                       aria-label="Delete upload"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          deleteOwnAsset(a);
                                       }}
                                    >
                                       ×
                                    </TileDelBtn>
                                 )}
                              </GalleryItem>
                           ))}
                        </ImageGallery>
                        <FieldLabel>
                           Video dim — {Math.round(value.background.opacity * 100)}%
                        </FieldLabel>
                        <Range
                           type="range"
                           min={0.1}
                           max={1}
                           step={0.05}
                           value={value.background.opacity}
                           onChange={(e) =>
                              setBg({ opacity: Number(e.target.value) })
                           }
                        />
                        <SecHint>
                           MP4/WebM up to 15MB. Customers download the video
                           over your WiFi each visit — keep loops short and
                           small.
                        </SecHint>
                     </>
                  ) : (
                     <>
                        <SecHint>
                           Tap an image to use it as the portal background —
                           from the library or your own uploads.
                        </SecHint>
                        <ImageGallery $portrait>
                           <UploadTile
                              kind="background"
                              onDone={(url) => {
                                 setBg({ mode: 'image', imageUrl: url });
                                 refreshMedia();
                              }}
                           />
                           {bgAssets.map((a) => (
                              <GalleryItem
                                 key={a._id}
                                 type="button"
                                 $checked={value.background.imageUrl === a.url}
                                 onClick={() => setBg({ mode: 'image', imageUrl: a.url })}
                              >
                                 <GalleryImg src={a.url} alt={a.name || 'background'} loading="lazy" />
                                 {value.background.imageUrl === a.url && (
                                    <CheckBadge>✓</CheckBadge>
                                 )}
                                 {!a.global && <MineBadge>Mine</MineBadge>}
                                 {canDeleteAsset(a) && (
                                    <TileDelBtn
                                       type="button"
                                       aria-label="Delete upload"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          deleteOwnAsset(a);
                                       }}
                                    >
                                       ×
                                    </TileDelBtn>
                                 )}
                              </GalleryItem>
                           ))}
                           {legacyBgUrl && (
                              <GalleryItem
                                 key="legacy-bg"
                                 type="button"
                                 $checked
                                 onClick={() => setBg({ imageUrl: '' })}
                              >
                                 <GalleryImg src={legacyBgUrl} alt="saved background" loading="lazy" />
                                 <CheckBadge>✓</CheckBadge>
                                 <MineBadge>Saved</MineBadge>
                              </GalleryItem>
                           )}
                        </ImageGallery>
                        <FieldLabel>
                           Image opacity — {Math.round(value.background.opacity * 100)}%
                        </FieldLabel>
                        <Range
                           type="range"
                           min={0.1}
                           max={1}
                           step={0.05}
                           value={value.background.opacity}
                           onChange={(e) =>
                              setBg({ opacity: Number(e.target.value) })
                           }
                        />
                        <SecHint>
                           Lower opacity fades the image so text stays readable.
                        </SecHint>
                     </>
                  )}
               </Section>
               )}

               {/* ── Image slider ─────────────────────────────────── */}
               {tab === 'slider' && (
               <Section>
                  <SecTitleRow>
                     <SecTitle>Image slider</SecTitle>
                     <Switch
                        $on={value.slider.enabled}
                        onClick={() =>
                           setSlider({ enabled: !value.slider.enabled })
                        }
                        role="switch"
                        aria-checked={value.slider.enabled}
                     >
                        <SwitchKnob $on={value.slider.enabled} />
                     </Switch>
                  </SecTitleRow>
                  {value.slider.enabled && (
                     <>
                        <SecHint>
                           Check the banners to rotate on the portal — from
                           the library or your own uploads. Pick as many as
                           you like.
                        </SecHint>
                        <ImageGallery>
                           <UploadTile
                              kind="slider"
                              onDone={(url) => {
                                 toggleSliderImage(url);
                                 refreshMedia();
                              }}
                           />
                           {sliderAssets.map((a) => (
                              <GalleryItem
                                 key={a._id}
                                 type="button"
                                 $checked={value.slider.images.includes(a.url)}
                                 onClick={() => toggleSliderImage(a.url)}
                              >
                                 <GalleryImg src={a.url} alt={a.name || 'banner'} loading="lazy" />
                                 {value.slider.images.includes(a.url) && (
                                    <CheckBadge>✓</CheckBadge>
                                 )}
                                 {!a.global && <MineBadge>Mine</MineBadge>}
                                 {canDeleteAsset(a) && (
                                    <TileDelBtn
                                       type="button"
                                       aria-label="Delete upload"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          deleteOwnAsset(a);
                                       }}
                                    >
                                       ×
                                    </TileDelBtn>
                                 )}
                              </GalleryItem>
                           ))}
                           {legacySliderUrls.map((u, i) => (
                              <GalleryItem
                                 key={`legacy-${i}`}
                                 type="button"
                                 $checked
                                 onClick={() => toggleSliderImage(u)}
                              >
                                 <GalleryImg src={u} alt="saved banner" loading="lazy" />
                                 <CheckBadge>✓</CheckBadge>
                                 <MineBadge>Saved</MineBadge>
                              </GalleryItem>
                           ))}
                        </ImageGallery>
                        {value.slider.images.length > 1 && (
                           <>
                              <FieldLabel>
                                 Order — drag, or use the arrows
                              </FieldLabel>
                              <OrderStrip
                                 onDragOver={(e) => e.preventDefault()}
                                 onDrop={() => {
                                    if (dragIdxRef.current !== null) {
                                       moveSliderImage(
                                          dragIdxRef.current,
                                          value.slider.images.length - 1
                                       );
                                    }
                                    dragIdxRef.current = null;
                                 }}
                              >
                                 {value.slider.images.map((u, i) => (
                                    <OrderItem
                                       key={`${u}-${i}`}
                                       draggable
                                       onDragStart={() => {
                                          dragIdxRef.current = i;
                                       }}
                                       onDragOver={(e) => e.preventDefault()}
                                       onDrop={(e) => {
                                          e.stopPropagation();
                                          if (dragIdxRef.current !== null) {
                                             moveSliderImage(dragIdxRef.current, i);
                                          }
                                          dragIdxRef.current = null;
                                       }}
                                       onDragEnd={() => {
                                          dragIdxRef.current = null;
                                       }}
                                    >
                                       <OrderNum>{i + 1}</OrderNum>
                                       <OrderImg src={u} alt={`slide ${i + 1}`} />
                                       <OrderArrows>
                                          <ArrowBtn
                                             type="button"
                                             disabled={i === 0}
                                             onClick={() => moveSliderImage(i, i - 1)}
                                             aria-label="Move earlier"
                                          >
                                             ◀
                                          </ArrowBtn>
                                          <ArrowBtn
                                             type="button"
                                             disabled={i === value.slider.images.length - 1}
                                             onClick={() => moveSliderImage(i, i + 1)}
                                             aria-label="Move later"
                                          >
                                             ▶
                                          </ArrowBtn>
                                       </OrderArrows>
                                    </OrderItem>
                                 ))}
                              </OrderStrip>
                           </>
                        )}
                        <FieldLabel>
                           Slider height — {value.slider.height}px
                        </FieldLabel>
                        <Range
                           type="range"
                           min={100}
                           max={400}
                           step={10}
                           value={value.slider.height}
                           onChange={(e) =>
                              setSlider({ height: Number(e.target.value) })
                           }
                        />
                     </>
                  )}
               </Section>
               )}

               {/* ── Sounds ───────────────────────────────────────── */}
               {tab === 'sounds' && (
               <>
                  <SoundPicker
                     label="Insert-coin sound"
                     category="insert-coin"
                     assets={insertCoinSounds}
                     selected={value.audio.insertCoinMp3}
                     onSelect={(url) => setAudio({ insertCoinMp3: url })}
                     onUploaded={(url) => {
                        setAudio({ insertCoinMp3: url });
                        refreshMedia();
                     }}
                     onDeleteOwn={deleteOwnAsset}
                  />
                  <SoundPicker
                     label="Countdown sound"
                     category="countdown"
                     assets={countdownSounds}
                     selected={value.audio.countdownMp3}
                     onSelect={(url) => setAudio({ countdownMp3: url })}
                     onUploaded={(url) => {
                        setAudio({ countdownMp3: url });
                        refreshMedia();
                     }}
                     onDeleteOwn={deleteOwnAsset}
                  />
               </>
               )}

               {/* ── Effects ──────────────────────────────────────── */}
               {tab === 'effects' && (
               <Section>
                  <SecHint>Animated background effects. Pick any.</SecHint>
                  <ChipWrap>
                     {PORTAL_EFFECTS.map((fx) => (
                        <Chip
                           key={fx.key}
                           type="button"
                           $active={value.effects.includes(fx.key)}
                           onClick={() => toggleEffect(fx.key)}
                        >
                           <span>{fx.emoji}</span> {fx.name}
                        </Chip>
                     ))}
                  </ChipWrap>
               </Section>
               )}
            </Bodied>

            {progress && <ProgressSlot>{progress}</ProgressSlot>}
            <Foot>
               {onPreview && (
                  <GhostBtn type="button" onClick={onPreview}>
                     Preview ↗
                  </GhostBtn>
               )}
               {deviceId && !hideLayoutTab && (
                  <GhostBtn type="button" onClick={() => setView('apply')}>
                     Apply to other vendos
                  </GhostBtn>
               )}
               <Spacer />
               {onSave && (
                  <PrimaryBtn
                     type="button"
                     onClick={() => onSave()}
                     disabled={saving}
                  >
                     {saving ? 'Saving…' : 'Save changes'}
                  </PrimaryBtn>
               )}
            </Foot>
            </>
            )}
         </Dialog>
      </Overlay>
   );

   return createPortal(body, document.body);
};

/* ── Upload tile (image galleries) — uploads then registers in the
      operator's own library so it shows with a "Mine" badge next time. ── */
const UploadTile: React.FC<{
   kind: 'background' | 'slider';
   onDone: (url: string) => void;
}> = ({ kind, onDone }) => {
   const inputRef = useRef<HTMLInputElement>(null);
   const [uploading, setUploading] = useState(false);
   const [progress, setProgress] = useState(0);

   const [fileNo, setFileNo] = useState<[number, number] | null>(null);

   const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (!files.length) return;
      setUploading(true);
      try {
         for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.size > 8 * 1024 * 1024) {
               toast.error(`${file.name}: too large — max 8MB`);
               continue;
            }
            setFileNo([i + 1, files.length]);
            setProgress(0);
            try {
               const { imageUrl } = await uploadImage(file, setProgress);
               await registerMedia({ kind, url: imageUrl, name: file.name });
               onDone(imageUrl);
            } catch (err: any) {
               toast.error(err?.response?.data?.error || `${file.name}: upload failed`);
            }
         }
      } finally {
         setUploading(false);
         setProgress(0);
         setFileNo(null);
      }
   };

   return (
      <UploadBox
         type="button"
         onClick={() => inputRef.current?.click()}
         disabled={uploading}
      >
         <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onPick}
            style={{ display: 'none' }}
         />
         {uploading
            ? `${fileNo ? `${fileNo[0]}/${fileNo[1]} · ` : ''}${progress}%`
            : '⬆ Upload'}
      </UploadBox>
   );
};

/* Background-video upload tile — R2 presign flow, NOT Cloudinary (see
   uploadVideoAsset). Single file; parent validates type/size + registers. */
const VideoUploadTile: React.FC<{
   busy: boolean;
   onPick: (file: File) => void;
}> = ({ busy, onPick }) => {
   const inputRef = useRef<HTMLInputElement>(null);
   return (
      <UploadBox
         type="button"
         onClick={() => inputRef.current?.click()}
         disabled={busy}
      >
         <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/webm"
            onChange={(e) => {
               const file = e.target.files?.[0];
               e.target.value = '';
               if (file) onPick(file);
            }}
            style={{ display: 'none' }}
         />
         {busy ? 'Uploading…' : '⬆ Upload'}
      </UploadBox>
   );
};

/* ── Sound picker (per slot): default + library/own sounds as radio rows ── */
const SoundPicker: React.FC<{
   label: string;
   assets: MediaAsset[];
   selected: string;
   onSelect: (url: string) => void;
   onUploaded: (url: string) => void;
   onDeleteOwn?: (a: MediaAsset) => void;
   canDelete?: (a: MediaAsset) => boolean;
   /** Slot this picker feeds — uploads are auto-categorized to it. */
   category?: 'countdown' | 'insert-coin';
}> = ({ label, assets, selected, onSelect, onUploaded, onDeleteOwn, canDelete, category }) => {
   const inputRef = useRef<HTMLInputElement>(null);
   const [uploading, setUploading] = useState(false);
   const [progress, setProgress] = useState(0);

   const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (file.size > 15 * 1024 * 1024) {
         toast.error('Audio too large — max 15MB');
         return;
      }
      setUploading(true);
      setProgress(0);
      try {
         const { audioUrl } = await uploadAudio(file, setProgress);
         await registerMedia({ kind: 'sound', url: audioUrl, name: file.name, category });
         onUploaded(audioUrl);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Audio upload failed');
      } finally {
         setUploading(false);
         setProgress(0);
      }
   };

   return (
      <Section>
         <SecTitle>{label}</SecTitle>
         <SoundRow
            type="button"
            $checked={!selected}
            onClick={() => onSelect('')}
         >
            <SoundCheck $checked={!selected}>{!selected ? '✓' : ''}</SoundCheck>
            <SoundName>Default sound</SoundName>
         </SoundRow>
         {assets.map((a) => (
            <SoundRow
               key={a._id}
               type="button"
               $checked={selected === a.url}
               onClick={() => onSelect(a.url)}
            >
               <SoundCheck $checked={selected === a.url}>
                  {selected === a.url ? '✓' : ''}
               </SoundCheck>
               <SoundName>
                  {a.name || 'Sound'}
                  {!a.global && <MineTag> · mine</MineTag>}
               </SoundName>
               <audio
                  src={a.url}
                  controls
                  preload="none"
                  onClick={(e) => e.stopPropagation()}
                  onPlay={pauseOtherAudio}
               />
               {onDeleteOwn && (canDelete ? canDelete(a) : !a.global) && (
                  <SoundDel
                     type="button"
                     aria-label="Delete upload"
                     onClick={(e) => {
                        e.stopPropagation();
                        onDeleteOwn(a);
                     }}
                  >
                     ×
                  </SoundDel>
               )}
            </SoundRow>
         ))}
         <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            onChange={onPick}
            style={{ display: 'none' }}
         />
         <AudioPick
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
         >
            {uploading ? `Uploading… ${progress}%` : '⬆ Upload mp3'}
         </AudioPick>
      </Section>
   );
};

/* ── styles (clone of the bulk-print drawer shell) ────────────────── */
const slideIn = keyframes`
   from { transform: translateX(100%); }
   to { transform: translateX(0); }
`;

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.45);
   z-index: 2100;
   display: flex;
   justify-content: flex-end;
`;

const Dialog = styled.div`
   width: 440px;
   max-width: 100%;
   height: 100%;
   background: var(--app-surface, ${colors.WHITE});
   border-left: 1px solid ${colors.GRAY12};
   display: flex;
   flex-direction: column;
   overflow: hidden;
   animation: ${slideIn} 0.2s ease-out;
`;

const Head = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   padding: 16px 18px 12px;
   background: ${colors.GRAY14};
   flex-shrink: 0;
`;

const Title = styled.h3`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.GRAY1};
`;

const Sub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const Close = styled.button`
   background: transparent;
   border: none;
   font-size: 24px;
   line-height: 1;
   color: ${colors.GRAY5};
   cursor: pointer;
   padding: 0 4px;
   &:hover { color: ${colors.GRAY1}; }
`;

const TabBar = styled.div`
   display: flex;
   gap: 4px;
   padding: 0 12px 10px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY12};
   flex-shrink: 0;
   overflow-x: auto;
`;

const TabBtn = styled.button<{ $active: boolean }>`
   border: none;
   border-radius: 999px;
   padding: 7px 13px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? '#fff' : colors.GRAY3)};
`;

const Bodied = styled.div`
   flex: 1 1 auto;
   overflow-y: auto;
   padding: 16px 18px;
   display: flex;
   flex-direction: column;
   gap: 22px;
`;

const Section = styled.section`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

/* Background presets — swatch tiles */
const PresetGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
   margin-bottom: 14px;
`;

const PresetTile = styled.button<{ $active: boolean }>`
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   border-radius: 10px;
   padding: 3px;
   background: transparent;
   cursor: pointer;
   text-align: center;
`;

const PresetSwatch = styled.div`
   height: 44px;
   border-radius: 7px;
   box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.25);
`;

const AccentSwatch = styled.div`
   height: 44px;
   border-radius: 7px;
   box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.25);
   display: flex;
   align-items: center;
   justify-content: center;
`;

const AccentDot = styled.span`
   width: 16px;
   height: 16px;
   border-radius: 50%;
   background: rgba(255, 255, 255, 0.85);
   box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

const PresetName = styled.div`
   margin-top: 4px;
   font-size: 10px;
   font-weight: 600;
   color: ${colors.GRAY3};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

/* ── Styles tab: per-button editor cards ──────────────────────────── */

const StyleCard = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 12px;
   margin-bottom: 12px;
   background: var(--app-surface, ${colors.WHITE});
`;

const StyleCardHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 8px;
`;

const StyleCardTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
`;

/* Live preview strip — checkerboard-free neutral field so the button
   color reads honestly on light and dark. */
const BtnPreviewWrap = styled.div`
   display: flex;
   justify-content: center;
   padding: 12px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   margin-bottom: 10px;
`;

const BtnPreview = styled.span<{ $shape: string; $color: string }>`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   min-width: 150px;
   padding: 10px 18px;
   font-size: 13.5px;
   font-weight: 700;
   border-radius: ${({ $shape }) => ($shape === 'pill' ? '999px' : '10px')};
   ${({ $shape, $color }) =>
      $shape === 'outline'
         ? css`
              background: transparent;
              border: 2px solid ${$color};
              color: ${$color};
           `
         : css`
              background: ${$color};
              border: 2px solid transparent;
              color: #ffffff;
           `}
`;

const StyleGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
`;

const StyleField = styled.div`
   min-width: 0;
`;

const ColorFieldRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

const MiniReset = styled.button`
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: ${colors.GRAY3};
   width: 30px;
   height: 30px;
   border-radius: 8px;
   font-size: 15px;
   cursor: pointer;
   &:hover {
      color: ${colors.PRIMARY};
      border-color: ${colors.PRIMARY};
   }
`;

const SelectInput = styled.select`
   width: 100%;
   padding: 9px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: var(--app-surface, ${colors.WHITE});
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;

const SecTitleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
`;

const SecTitle = styled.h4`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const SecHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const LayoutGrid = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const LayoutCard = styled.button<{ $active: boolean }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 10px;
   border-radius: 10px;
   border: 1.5px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $active }) =>
      $active ? `${colors.PRIMARY}10` : 'transparent'};
   cursor: pointer;
   text-align: left;
   transition: all 0.12s;
   &:hover { border-color: ${colors.PRIMARY}; }
   &:disabled {
      opacity: 0.5;
      cursor: default;
      &:hover { border-color: ${colors.GRAY12}; }
   }
`;

/* Mini page sketch: title strip, status dot, two button bars. */
const LayoutSwatch = styled.div`
   width: 56px;
   height: 64px;
   border-radius: 8px;
   flex-shrink: 0;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 5px;
   padding: 6px;
   border: 1px solid ${colors.GRAY12};
`;

const SwatchStrip = styled.div`
   width: 100%;
   height: 7px;
   border-radius: 3px;
`;

const SwatchDot = styled.div`
   width: 10px;
   height: 10px;
   border-radius: 50%;
`;

const SwatchBar = styled.div`
   width: 80%;
   height: 8px;
   border-radius: 999px;
`;

const DecorSwatch = styled.div`
   width: 44px;
   height: 44px;
   border-radius: 50%;
   flex-shrink: 0;
   border: 1px solid ${colors.GRAY12};
`;

const LayoutText = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
`;

const LayoutName = styled.span`
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.GRAY1};
   line-height: 1.2;
`;

const LayoutDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.35;
`;

const SegToggle = styled.div`
   display: inline-flex;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
   width: fit-content;
`;

const SegBtn = styled.button<{ $active: boolean }>`
   padding: 7px 18px;
   font-size: 12px;
   font-weight: 600;
   border: none;
   cursor: pointer;
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? '#fff' : colors.GRAY3)};
`;

const Row = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const ColorSwatch = styled.input`
   width: 44px;
   height: 36px;
   padding: 2px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: none;
   cursor: pointer;
`;

const TextInput = styled.input`
   flex: 1;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   color: ${colors.GRAY1};
   background: var(--app-surface, ${colors.WHITE});
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;

const Range = styled.input`
   width: 100%;
   accent-color: ${colors.PRIMARY};
`;

const ResetLink = styled.button`
   border: none;
   background: transparent;
   color: ${colors.GRAY5};
   font-size: 11.5px;
   font-weight: 600;
   cursor: pointer;
   text-decoration: underline;
   &:hover { color: ${colors.PRIMARY}; }
`;

const Switch = styled.button<{ $on: boolean }>`
   width: 40px;
   height: 22px;
   border-radius: 12px;
   border: none;
   cursor: pointer;
   padding: 2px;
   display: flex;
   justify-content: ${({ $on }) => ($on ? 'flex-end' : 'flex-start')};
   background: ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   transition: background 0.15s;
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 50%;
   background: #fff;
   transition: all 0.15s;
`;

/* ── media galleries ──────────────────────────────────────────────── */

/* $portrait: phone-wallpaper aspect for full-screen media (backgrounds,
   background videos). Slider banners stay landscape. */
const ImageGallery = styled.div<{ $portrait?: boolean }>`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
   ${({ $portrait }) =>
      $portrait &&
      css`
         > * {
            height: auto !important;
            aspect-ratio: 9 / 16;
         }
      `}
`;

const GalleryItem = styled.button<{ $checked: boolean }>`
   position: relative;
   height: 64px;
   border-radius: 8px;
   overflow: hidden;
   padding: 0;
   cursor: pointer;
   border: 2px solid
      ${({ $checked }) => ($checked ? colors.PRIMARY : colors.GRAY12)};
   background: ${colors.GRAY14};
`;

const GalleryImg = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
   display: block;
`;

const GalleryVideo = styled.video`
   width: 100%;
   height: 100%;
   object-fit: cover;
   display: block;
   pointer-events: none;
   /* iOS draws its own centred play glyph over a video element even with no
      controls. On a tile that looks like a broken thumbnail. */
   &::-webkit-media-controls,
   &::-webkit-media-controls-start-playback-button {
      display: none !important;
      -webkit-appearance: none;
   }
`;

const CheckBadge = styled.div`
   position: absolute;
   top: 4px;
   right: 4px;
   width: 18px;
   height: 18px;
   border-radius: 50%;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 11px;
   font-weight: 800;
   display: flex;
   align-items: center;
   justify-content: center;
`;

const MineBadge = styled.div`
   position: absolute;
   bottom: 4px;
   left: 4px;
   background: rgba(0, 0, 0, 0.6);
   color: #fff;
   font-size: 9px;
   font-weight: 700;
   border-radius: 4px;
   padding: 1px 5px;
`;

const TileDelBtn = styled.button`
   position: absolute;
   top: 4px;
   left: 4px;
   width: 18px;
   height: 18px;
   border-radius: 50%;
   border: none;
   background: rgba(0, 0, 0, 0.65);
   color: #fff;
   font-size: 12px;
   line-height: 1;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: center;
`;

const OrderStrip = styled.div`
   display: flex;
   gap: 8px;
   overflow-x: auto;
   padding-bottom: 4px;
`;

const OrderItem = styled.div`
   position: relative;
   flex: 0 0 auto;
   width: 86px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
   cursor: grab;
   background: ${colors.GRAY14};
`;

const OrderImg = styled.img`
   width: 100%;
   height: 48px;
   object-fit: cover;
   display: block;
   pointer-events: none;
`;

const OrderNum = styled.div`
   position: absolute;
   top: 3px;
   left: 3px;
   background: rgba(0, 0, 0, 0.65);
   color: #fff;
   font-size: 10px;
   font-weight: 800;
   border-radius: 4px;
   padding: 1px 5px;
   z-index: 1;
`;

const OrderArrows = styled.div`
   display: flex;
`;

const ArrowBtn = styled.button`
   flex: 1;
   border: none;
   background: transparent;
   color: ${colors.GRAY3};
   font-size: 11px;
   padding: 3px 0;
   cursor: pointer;
   &:hover:not(:disabled) { color: ${colors.PRIMARY}; }
   &:disabled { opacity: 0.3; cursor: default; }
`;

const ResetTopRow = styled.div`
   display: flex;
   justify-content: flex-end;
   margin-bottom: 2px;
`;

const LayoutResetLink = styled.button`
   margin-top: 0;
   font-size: 11px;
   opacity: 0.75;
   border: none;
   background: transparent;
   padding: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY3};
   cursor: pointer;
   text-decoration: underline;
   text-underline-offset: 2px;
   &:hover {
      color: ${colors.DANGER};
   }
   &:disabled {
      opacity: 0.4;
      cursor: default;
   }
`;

const DefaultsNote = styled.p<{ $failed: boolean }>`
   margin: 0 0 8px;
   padding: 8px 10px;
   border-radius: 8px;
   font-size: 12px;
   line-height: 1.5;
   color: ${({ $failed }) => ($failed ? colors.DANGER : colors.GRAY3)};
   background: ${({ $failed }) => ($failed ? `${colors.DANGER}14` : `${colors.GRAY12}66`)};
   border: 1px solid ${({ $failed }) => ($failed ? `${colors.DANGER}44` : colors.GRAY12)};
`;

const RetryLink = styled.button`
   border: none;
   background: transparent;
   padding: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   text-decoration: underline;
   text-underline-offset: 2px;
`;

const ResetDoneNote = styled.p`
   margin: 0 0 8px;
   padding: 8px 10px;
   border-radius: 8px;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.SUCCESS};
   background: ${colors.SUCCESS}14;
   border: 1px solid ${colors.SUCCESS}44;
`;

/* ── Apply to other vendos ─────────────────────────────────────── */
const ApplyRow = styled.button<{ $checked: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   width: 100%;
   text-align: left;
   margin-top: 8px;
   padding: 10px 12px;
   border-radius: 8px;
   cursor: pointer;
   border: 1.5px solid
      ${({ $checked }) => ($checked ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $checked }) =>
      $checked ? `${colors.PRIMARY}0d` : 'transparent'};
`;

const ApplyName = styled.span`
   font-size: 13px;
   font-weight: 600;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const ApplyEmpty = styled.div`
   margin-top: 8px;
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const SoundDel = styled.button`
   border: none;
   background: transparent;
   color: ${colors.GRAY5};
   font-size: 18px;
   line-height: 1;
   cursor: pointer;
   padding: 0 2px;
   flex-shrink: 0;
   &:hover { color: ${colors.DANGER}; }
`;

const UploadBox = styled.button`
   height: 64px;
   border-radius: 8px;
   border: 1.5px dashed ${colors.GRAY12};
   background: transparent;
   color: ${colors.GRAY3};
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY};
      color: ${colors.PRIMARY};
   }
   &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SoundRow = styled.button<{ $checked: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   width: 100%;
   text-align: left;
   padding: 7px 8px;
   border-radius: 8px;
   cursor: pointer;
   border: 1.5px solid
      ${({ $checked }) => ($checked ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $checked }) =>
      $checked ? `${colors.PRIMARY}0d` : 'transparent'};
   audio { height: 28px; max-width: 160px; margin-left: auto; }
`;

const SoundCheck = styled.span<{ $checked: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 50%;
   flex-shrink: 0;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 11px;
   font-weight: 800;
   color: #fff;
   background: ${({ $checked }) =>
      $checked ? colors.PRIMARY : colors.GRAY12};
`;

const SoundName = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const MineTag = styled.span`
   font-size: 10.5px;
   font-weight: 600;
   color: ${colors.GRAY5};
`;

const AudioPick = styled.button`
   padding: 9px 12px;
   border: 1.5px dashed ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY};
      color: ${colors.PRIMARY};
   }
   &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ChipWrap = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
`;

const Chip = styled.button<{ $active: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 6px 10px;
   border-radius: 999px;
   font-size: 11.5px;
   font-weight: 600;
   cursor: pointer;
   border: 1.5px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $active }) =>
      $active ? `${colors.PRIMARY}12` : 'transparent'};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const ProgressSlot = styled.div`
   padding: 0 18px 6px;
   flex-shrink: 0;
   background: ${colors.GRAY14};
   border-top: 1px solid ${colors.GRAY12};
   > * {
      margin-top: 8px;
   }
`;

const Foot = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 12px 18px;
   border-top: 1px solid ${colors.GRAY12};
   background: ${colors.GRAY14};
   flex-shrink: 0;
`;

const Spacer = styled.div`
   flex: 1;
`;

const GhostBtn = styled.button`
   padding: 8px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover { border-color: ${colors.PRIMARY}; color: ${colors.PRIMARY}; }
`;

const PrimaryBtn = styled.button`
   padding: 8px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export default PortalAdvancedDrawer;
